import { useState, useRef, useEffect } from 'react';
import { Chip, Spinner } from '@blacknbrownstudios/ui';
import type { ServerInfo } from '../types';
import { MCP_PRESETS } from '../config/presets';

interface ServerSelectorProps {
  availableServers: ServerInfo[];
  enabledServers: string[];
  onToggle: (serverName: string) => void;
  isLoading?: boolean;
  error?: string | null;
  activePreset?: string | null;
  onPresetChange?: (presetId: string) => void;
}

export function ServerSelector({
  availableServers,
  enabledServers,
  onToggle,
  isLoading = false,
  error = null,
  activePreset = null,
  onPresetChange,
}: ServerSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const enabledCount = enabledServers.length;
  const totalCount = availableServers.length;
  const currentPreset = MCP_PRESETS.find((p) => p.id === activePreset);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-neutral-500">
        <Spinner size="small" color="inherit" />
        <span>Loading servers...</span>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-red-400" title={error}>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <span>MCP unavailable</span>
      </div>
    );
  }

  if (totalCount === 0) {
    return (
      <div
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-neutral-500"
        title="MCP tool servers are not configured for this deployment. Chat works without them."
      >
        <span>No MCP servers</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800 rounded transition-colors"
        title="Select MCP servers"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2"
          />
        </svg>
        <span>{currentPreset ? currentPreset.label : `${enabledCount}/${totalCount}`}</span>
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 mb-1 w-72 bg-neutral-800 border border-neutral-700 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
          {/* Presets Section */}
          <div className="p-2 border-b border-neutral-700">
            <div className="text-xs text-neutral-400 font-medium mb-2">Quick Presets</div>
            <div className="flex flex-wrap gap-1">
              {MCP_PRESETS.map((preset) => {
                const isActive = activePreset === preset.id;
                return (
                  <button
                    key={preset.id}
                    onClick={() => onPresetChange?.(preset.id)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
                    }`}
                    title={preset.description}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Header */}
          <div className="p-2 border-b border-neutral-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-neutral-400 font-medium">MCP Servers</div>
                <div className="text-xs text-neutral-500">
                  {enabledCount} of {totalCount} enabled
                </div>
              </div>
              {!activePreset && (
                <Chip label="Custom" size="small" color="warning" variant="outlined" />
              )}
            </div>
          </div>

          {/* Server List */}
          <div className="p-1">
            {availableServers.map((server) => {
              const isEnabled = enabledServers.includes(server.name);
              return (
                <button
                  key={server.name}
                  onClick={() => onToggle(server.name)}
                  className={`w-full flex items-start gap-2 px-2 py-2 rounded text-left transition-colors ${
                    isEnabled
                      ? 'bg-primary-900/30 hover:bg-primary-900/50'
                      : 'hover:bg-neutral-700/50'
                  }`}
                >
                  <div
                    className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                      isEnabled ? 'bg-primary-600 border-primary-600' : 'border-neutral-600'
                    }`}
                  >
                    {isEnabled && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-sm font-medium ${isEnabled ? 'text-white' : 'text-neutral-300'}`}
                    >
                      {server.name}
                    </div>
                    <Chip
                      label={`${server.tools.length} tools`}
                      size="small"
                      color="default"
                      variant="outlined"
                      className="mt-1"
                    />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="p-2 border-t border-neutral-700 flex justify-between">
            <button
              onClick={() => onPresetChange?.('all')}
              className="text-xs text-primary-400 hover:text-primary-300"
            >
              Enable all
            </button>
            <button
              onClick={() => onPresetChange?.('none')}
              className="text-xs text-neutral-400 hover:text-neutral-300"
            >
              Disable all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
